import { Link, NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
    HiMenu,
    HiX,
    HiMail,
    HiLocationMarker,
    HiChevronDown,
    HiShoppingCart
} from 'react-icons/hi';
import useAuthStore from '@store/authStore';
import useCartStore from '@store/cartStore';
import NotificationBell from './NotificationBell';
import { getAvatarUrl } from '@/utils/avatarUtils';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { items } = useCartStore();
    const cartCount = items.length;
    const { isAuthenticated, user } = useAuthStore();
    const location = useLocation();
    const isHomePage = location.pathname === '/';
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navigation = [
        {
            name: 'Home',
            href: '/',
            children: [
                { name: 'About', href: '/#about' },
                { name: 'Pillars', href: '/#pillars' },
                { name: 'Why VOE', href: '/#whyvoe' },
                { name: 'How it Works', href: '/#howitwork' },
            ]
        },
        { name: 'Audit & Assessment', href: '/audit' },
        { name: 'The Seal & VOE Directory', href: '/the-seal-voe-directory' },
        {
            name: 'VOE Assessors',
            href: '/assessors',
            children: isAuthenticated && ['admin', 'candidate', 'certified_vendor', 'studio'].includes(user?.role) ? [
                { name: 'Become an Assessor', href: '/assessors' },
                { name: 'Assessor Directory', href: '/dashboard/assessors-directory' }
            ] : null
        },
        { name: 'Our products', href: '/products' },
        { name: 'Q&A', href: '/qa' },
        { name: 'Contact', href: '/contact' },
    ];



    return (
        <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled || !isHomePage ? 'bg-white shadow-md' : 'bg-transparent'
            }`}>
            {/* Top bar */}
            <div className={`transition-all duration-300 ${isScrolled || !isHomePage ? 'bg-secondary-900 py-2' : 'bg-black/20 backdrop-blur-sm py-1'
                }`}>
                <div className="w-full px-4 sm:px-6 lg:px-12 flex flex-wrap justify-between items-center text-sm text-secondary-300">
                    <div className="flex items-center gap-4">
                        <a href="mailto:info@voe-standard.com" className="flex items-center gap-1 hover:text-white transition-colors">
                            <HiMail className="w-4 h-4" />
                            <span className="hidden sm:inline">info@voe-standard.com</span>
                        </a>
                    </div>
                    <div className="hidden md:flex items-center gap-1">
                        <HiLocationMarker className="w-4 h-4" />
                        <span>1301 N Broadway STE #32329, Los Angeles, CA 90012</span>
                    </div>
                </div>
            </div>

            {/* Main navigation */}
            <nav className="transition-all duration-300">
                <div className="w-full px-4 sm:px-6 lg:px-12">
                    <div className={`flex justify-between items-center transition-all duration-300 ${isScrolled || !isHomePage ? 'h-16' : 'h-24'
                        }`}>

                        {/* Left Section: Logo */}
                        <div className="flex-none flex items-center">
                            <Link to="/" className="flex items-center">
                                <img
                                    src={isHomePage && !isScrolled ? "/logos/Logo_Black_transparent.png" : "/logos/Logo_White_transparent.png"}
                                    alt="VOE Standard"
                                    className="h-10 md:h-12 w-auto transition-all duration-300"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 40"><text x="10" y="30" font-family="Arial" font-size="24" font-weight="bold" fill="%23d4af37">VOE</text></svg>';
                                    }}
                                />
                            </Link>
                        </div>

                        {/* Center Section: Desktop navigation */}
                        <div className="hidden lg:flex flex-1 items-center justify-center gap-2 xl:gap-6 px-8">
                            {navigation.map((item) => (
                                <div key={item.name} className="relative group">
                                    {item.children ? (
                                        <NavLink
                                            to={item.href}
                                            className={({ isActive }) =>
                                                `flex items-center text-xs xl:text-sm font-bold transition-colors py-2 ${isHomePage && !isScrolled ? 'text-white hover:text-gold-400' : (isActive ? 'text-gold-600' : 'text-secondary-700 hover:text-gold-600')}`
                                            }
                                        >
                                            {item.name}
                                            <HiChevronDown className="ml-1 w-4 h-4 group-hover:rotate-180 transition-transform duration-200" />
                                        </NavLink>
                                    ) : (
                                        <NavLink
                                            to={item.href}
                                            className={({ isActive }) =>
                                                `text-xs xl:text-sm font-bold transition-colors ${isHomePage && !isScrolled ? 'text-white hover:text-gold-400' : (isActive ? 'text-gold-600' : 'text-secondary-700 hover:text-gold-600')}`
                                            }
                                        >
                                            {item.name}
                                        </NavLink>
                                    )}

                                    {/* Dropdown Menu */}
                                    {item.children && (
                                        <div className="absolute top-full left-0 w-48 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50">
                                            <div className="bg-white rounded-xl shadow-lg border border-secondary-100 overflow-hidden">
                                                {item.children.map((child) => (
                                                    <Link
                                                        key={child.name}
                                                        to={child.href}
                                                        className="block px-4 py-3 text-sm text-secondary-600 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                                                    >
                                                        {child.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Right Section: Cart + Auth Buttons + Mobile Toggle */}
                        <div className="flex-none flex items-center justify-end gap-4 lg:gap-6">
                            <div className="hidden lg:flex items-center gap-6">
                                {/* Cart Icon */}
                                <Link to="/cart" className={`relative p-2 transition-colors ${isHomePage && !isScrolled ? 'text-white hover:text-gold-400' : 'text-secondary-600 hover:text-gold-600'}`}>
                                    <HiShoppingCart className="w-6 h-6" />
                                    {cartCount > 0 && (
                                        <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-secondary-900 bg-gold-500 rounded-full">
                                            {cartCount}
                                        </span>
                                    )}
                                </Link>

                                {isAuthenticated ? (
                                    <div className="flex items-center gap-4">
                                        <NotificationBell isScrolled={isScrolled} isHomePage={isHomePage} />
                                        <Link
                                            to="/dashboard"
                                            className={`flex items-center gap-2 text-sm font-bold transition-colors ${isHomePage && !isScrolled ? 'text-white hover:text-gold-400' : 'text-secondary-700 hover:gold-600'}`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center text-gold-700 font-bold overflow-hidden border border-gold-200">
                                                {(() => {
                                                    const avatarUrl = getAvatarUrl(user?.avatar);
                                                    return avatarUrl ? (
                                                        <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                                                    ) : (
                                                        user?.username?.[0]?.toUpperCase() || 'U'
                                                    );
                                                })()}
                                            </div>
                                            <span className="hidden xl:inline">My Account &rarr;</span>
                                        </Link>
                                    </div>
                                ) : (
                                    <>
                                        <Link to="/register" className="btn-cta text-sm shadow-md hover:shadow-lg px-6 py-2">
                                            Join VOE
                                        </Link>
                                        <Link to="/login" className={`text-sm font-bold transition-colors ${isHomePage && !isScrolled ? 'text-white hover:text-gold-400' : 'text-secondary-700 hover:text-gold-600'}`}>
                                            Login
                                        </Link>
                                    </>
                                )}
                            </div>

                            {/* Mobile menu button */}
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="lg:hidden p-2 rounded-lg text-secondary-600 hover:bg-secondary-100"
                            >
                                {isMenuOpen ? (
                                    <HiX className="w-6 h-6" />
                                ) : (
                                    <HiMenu className="w-6 h-6" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile menu */}
                {isMenuOpen && (
                    <div className="lg:hidden border-t border-secondary-200 bg-white animate-slide-down h-screen overflow-y-auto pb-20">
                        <div className="w-full px-4 sm:px-6 lg:px-12 py-4 space-y-1">
                            {navigation.map((item) => (
                                <div key={item.name}>
                                    {item.children ? (
                                        <div className="space-y-1">
                                            <div className="px-4 py-2 text-sm font-bold text-secondary-900 border-l-4 border-primary-500 bg-secondary-50">
                                                {item.name}
                                            </div>
                                            <div className="pl-4 space-y-1">
                                                {item.children.map((child) => (
                                                    <Link
                                                        key={child.name}
                                                        to={child.href}
                                                        onClick={() => setIsMenuOpen(false)}
                                                        className="block px-4 py-2 rounded-lg text-sm text-secondary-600 hover:bg-secondary-50"
                                                    >
                                                        {child.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <NavLink
                                            to={item.href}
                                            onClick={() => setIsMenuOpen(false)}
                                            className={({ isActive }) =>
                                                `block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-primary-50 text-primary-600' : 'text-secondary-700 hover:bg-secondary-50'
                                                }`
                                            }
                                        >
                                            {item.name}
                                        </NavLink>
                                    )}
                                </div>
                            ))}

                            <div className="my-4 border-t border-secondary-200"></div>

                            {/* Mobile Cart Link */}
                            <Link
                                to="/cart"
                                onClick={() => setIsMenuOpen(false)}
                                className="flex items-center justify-between px-4 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 rounded-lg"
                            >
                                <div className="flex items-center gap-2">
                                    <HiShoppingCart className="w-5 h-5" />
                                    <span>Cart</span>
                                </div>
                                {cartCount > 0 && (
                                    <span className="bg-accent-orange text-white text-xs px-2 py-0.5 rounded-full">
                                        {cartCount}
                                    </span>
                                )}
                            </Link>

                            <div className="pt-4 space-y-3 px-4">
                                {isAuthenticated ? (
                                    <>
                                        <div className="flex items-center gap-3 px-4 py-3 bg-secondary-50 rounded-lg mb-2">
                                            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
                                                {user?.username?.[0]?.toUpperCase() || 'U'}
                                            </div>
                                            <div>
                                                <p className="font-medium text-secondary-900">{user?.username}</p>
                                                <p className="text-xs text-secondary-500 truncate max-w-[150px]">{user?.email}</p>
                                            </div>
                                        </div>
                                        <Link
                                            to="/profile"
                                            onClick={() => setIsMenuOpen(false)}
                                            className="block w-full btn-primary text-center"
                                        >
                                            My Profile
                                        </Link>
                                        {/* Optional Admin Link if needed */}
                                        {['admin', 'candidate', 'certified_vendor', 'free_user'].includes(user?.role) && (
                                            <Link
                                                to="/dashboard"
                                                onClick={() => setIsMenuOpen(false)}
                                                className="block w-full btn-secondary text-center mt-2"
                                            >
                                                {user?.role === 'admin' ? 'Dashboard (Admin)' : 'Dashboard'}
                                            </Link>
                                        )}
                                        <button
                                            onClick={() => {
                                                useAuthStore.getState().logout();
                                                setIsMenuOpen(false);
                                            }}
                                            className="block w-full text-center px-4 py-2 text-sm text-red-600 hover:text-red-700"
                                        >
                                            Sign Out
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/register"
                                            onClick={() => setIsMenuOpen(false)}
                                            className="block w-full btn-primary text-center"
                                        >
                                            Join VOE
                                        </Link>
                                        <Link
                                            to="/login"
                                            onClick={() => setIsMenuOpen(false)}
                                            className="block w-full btn-secondary text-center"
                                        >
                                            Login
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
